import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useNavigationType, type LinkProps } from 'react-router-dom';

/**
 * Every "Back" returns to where the user came from, including the tab, filters and scroll position
 * (2026-10-28, after Credentials > "Back to Workforce" landed on the Applications tab).
 *
 * How: <NavMemory /> (mounted once inside the router) records, per browser tab, the full address of
 * each history entry by react-router's own history index (history.state.idx), so PUSH, REPLACE (tab
 * and filter changes) and POP all keep it exact. A Back control asks for the entry before the current
 * one: when it exists it goes back one step (the browser's own back, so the page's address, tab and
 * filters are exactly as left) and NavMemory puts the scroll position back once the page has drawn.
 * Only when there is no earlier page in this tab (a link from an email, a new tab, a reload of a
 * shared link) does it use the page's sensible default.
 */

const ENTRIES_KEY = 'rr.nav.entries.v1';
const SCROLL_KEY = 'rr.nav.scroll.v2';

type Entries = Record<string, string>;

function read(key: string): Entries {
  try {
    const raw = sessionStorage.getItem(key);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === 'object' ? (v as Entries) : {};
  } catch {
    return {};
  }
}

function write(key: string, v: Entries) {
  try {
    sessionStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* storage blocked: Back falls back to the default target */
  }
}

/** The first entry of a page load has the key "default": give it an id for this load only. */
const LOAD_ID = Math.random().toString(36).slice(2, 10);
function entryKey(key: string | undefined): string {
  return key && key !== 'default' ? key : `default-${LOAD_ID}`;
}

function historyIdx(): number | null {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' && idx >= 0 ? idx : null;
}

/** Pages a Back never returns to: entry and sign-in screens, and the catch-all redirect. */
const NOT_A_BACK_TARGET = /^\/(signin|login|signup|sso\/|employer-login|present)?(\?|$)/;

const withoutHash = (a: string) => a.split('#')[0];

/**
 * The page before this one in this browser tab, and how many history steps back it is: entries that are
 * this same page (a jump to a section with #, a tab that pushed) are skipped. Null when there is none.
 */
export function previousEntry(here: string): { address: string; steps: number } | null {
  const idx = historyIdx();
  if (idx == null || idx === 0) return null;
  const entries = read(ENTRIES_KEY);
  for (let j = idx - 1; j >= 0; j--) {
    const prev = entries[String(j)];
    if (!prev || !prev.startsWith('/') || prev.startsWith('//')) return null;
    if (withoutHash(prev) === withoutHash(here)) continue;
    if (NOT_A_BACK_TARGET.test(prev)) return null;
    return { address: prev, steps: idx - j };
  }
  return null;
}

/** Plain-words name of a page, for "Back to …". Longest prefix wins. */
const PAGE_NAMES: Array<[RegExp, string]> = [
  [/^\/employer\/workforce/, 'Workforce'],
  [/^\/employer\/credentials/, 'Credentials'],
  [/^\/employer\/team-onboarding/, 'Orientation'],
  [/^\/employer\/insights/, 'Insights'],
  [/^\/employer\/scheduling/, 'Scheduling'],
  [/^\/employer\/search/, 'Search'],
  [/^\/employer\/matches/, 'Matches'],
  [/^\/employer\/jobs\/[^/]+\/applicants/, 'Applicants'],
  [/^\/employer\/jobs/, 'Jobs'],
  [/^\/employer\/applications\//, 'the application'],
  [/^\/employer\/emt-profile\//, 'the profile'],
  [/^\/employer\/notifications/, 'Notifications'],
  [/^\/employer\/messages/, 'Messages'],
  [/^\/employer\/account/, 'Account'],
  [/^\/employer\/settings/, 'Settings'],
  [/^\/employer\/company-profile/, 'Company profile'],
  [/^\/employer\/company-checklist/, 'Company checklist'],
  [/^\/employer\/dashboard/, 'Dashboard'],
  [/^\/institute\/classes\/[^/]+(\?|$)/, 'the roster'],
  [/^\/institute\/classes/, 'Classes'],
  [/^\/institute\/renewals/, 'Renewals'],
  [/^\/institute\/dashboard/, 'Dashboard'],
  [/^\/institute\//, 'your institute'],
  [/^\/admin\/presenter/, 'Presenter'],
  [/^\/admin\//, 'Admin'],
  [/^\/jobs\/companies/, 'Companies'],
  [/^\/jobs\/[^/]+(\?|$)/, 'the job'],
  [/^\/jobs/, 'Jobs'],
  [/^\/applications/, 'Applications'],
  [/^\/career-wallet/, 'Career Wallet'],
  [/^\/notifications/, 'Notifications'],
  [/^\/messages/, 'Messages'],
  [/^\/orientation/, 'Orientation'],
  [/^\/find-training/, 'Find Training'],
  [/^\/account/, 'Account'],
  [/^\/profile/, 'My Profile'],
  [/^\/settings/, 'Settings'],
  [/^\/dashboard/, 'Dashboard'],
  [/^\/careers\//, 'the job board'],
  [/^\/org\//, 'the company page'],
  [/^\/institutes\//, 'the institute'],
  [/^\/p\//, 'the profile'],
];

export function pageName(address: string): string | null {
  const hit = PAGE_NAMES.find(([re]) => re.test(address));
  return hit ? hit[1] : null;
}

/**
 * Where a Back control on this page goes. `fallback` (with its tab, e.g.
 * '/employer/workforce?tab=active') and `fallbackLabel` ("Workforce") are used only when this tab has
 * no earlier page.
 */
export function useBackTarget(fallback: string, fallbackLabel: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const here = location.pathname + location.search;
  const prev = previousEntry(here);
  const usable = prev?.address ?? null;
  const steps = prev?.steps ?? 1;
  const label = `Back to ${(usable && pageName(usable)) || fallbackLabel}`;
  const href = usable || fallback;
  const go = useCallback(
    (e?: { preventDefault: () => void }) => {
      e?.preventDefault();
      if (usable) navigate(-steps);
      else navigate(fallback);
    },
    [usable, steps, fallback, navigate],
  );
  return { href, label, go, fromHistory: !!usable, steps };
}

/** "Back to …" link: same look as before, real href for open-in-new-tab, history-aware click. */
type BackLinkProps = Omit<LinkProps, 'to'> & {
  fallback: string;
  fallbackLabel: string;
  /** Optional icon (rendered before the label). */
  children?: ReactNode;
  testId?: string;
};

export const BackLink = forwardRef<HTMLAnchorElement, BackLinkProps>(function BackLink(
  { fallback, fallbackLabel, children, testId, onClick, ...rest },
  ref,
) {
  const { href, label, go, fromHistory } = useBackTarget(fallback, fallbackLabel);
  return (
    <Link
      {...rest}
      ref={ref}
      to={href}
      onClick={(e) => {
        onClick?.(e);
        // Ctrl/Cmd/Shift-click and middle-click keep opening the href in a new tab or window.
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        go(e);
      }}
      data-testid={testId || 'back-link'}
      data-back-from-history={fromHistory ? 'true' : 'false'}
    >
      {children}
      {label}
    </Link>
  );
});

/**
 * Mount once inside the router. Records each history entry's address and scroll position, and puts
 * the scroll position back when the user returns to an entry (browser back or a Back control).
 */
export function NavMemory() {
  const location = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef<string | null>(null);

  // Remember this entry's address (PUSH truncates later entries, REPLACE overwrites, POP re-reads).
  useLayoutEffect(() => {
    // Scroll positions are kept per history entry key: unique per entry and kept across a reload, so a
    // fresh page load (history index back at 0) can never pick up another page's position.
    keyRef.current = entryKey(location.key);
    const idx = historyIdx();
    if (idx == null) return;
    const entries = read(ENTRIES_KEY);
    if (navType === 'PUSH') {
      for (const k of Object.keys(entries)) if (Number(k) > idx) delete entries[k];
    }
    entries[String(idx)] = location.pathname + location.search + location.hash;
    write(ENTRIES_KEY, entries);
  }, [location, navType]);

  // Keep this entry's scroll position up to date. The entry and position are captured when the scroll
  // happens (not when the throttled write runs), so a quick click right after scrolling still saves the
  // position under the page being left, not the page being opened.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const pending = new Map<string, number>();
    const flush = () => {
      t = null;
      if (!pending.size) return;
      const scroll = read(SCROLL_KEY);
      for (const [key, y] of pending) {
        delete scroll[key];
        scroll[key] = String(y);
      }
      // Keep the newest 200 entries.
      const keys = Object.keys(scroll);
      for (const k of keys.slice(0, Math.max(0, keys.length - 200))) delete scroll[k];
      pending.clear();
      write(SCROLL_KEY, scroll);
    };
    const save = () => {
      const key = keyRef.current;
      if (!key) return;
      pending.set(key, Math.round(window.scrollY));
      if (!t) t = setTimeout(flush, 150);
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => {
      window.removeEventListener('scroll', save);
      if (t) clearTimeout(t);
      flush();
    };
  }, []);

  // Returning to an entry: restore its scroll once the page is tall enough (lists load after paint).
  useEffect(() => {
    if (navType !== 'POP' || location.hash) return;
    const y = Number(read(SCROLL_KEY)[entryKey(location.key)] || 0);
    if (!y) return;
    let tries = 0;
    let steady = 0;
    let lastHeight = -1;
    // The user scrolling or typing first wins.
    const stop = () => clearInterval(timer);
    window.addEventListener('wheel', stop, { once: true, passive: true });
    window.addEventListener('touchstart', stop, { once: true, passive: true });
    window.addEventListener('keydown', stop, { once: true });
    const timer = setInterval(() => {
      tries += 1;
      const height = document.documentElement.scrollHeight;
      steady = height === lastHeight ? steady + 1 : 0;
      lastHeight = height;
      const max = height - window.innerHeight;
      const reached = max >= y - 4;
      // Held until the page stops growing: content loading above the spot would otherwise carry the view
      // away from it (the browser keeps what is on screen in place as the page grows).
      if (reached || tries > 40) window.scrollTo(0, Math.min(y, Math.max(0, max)));
      if ((reached && steady >= 20) || (!reached && tries > 40) || tries > 100) clearInterval(timer);
    }, 75);
    return () => {
      clearInterval(timer);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
  }, [location, navType]);

  return null;
}
